import { GIFEncoder, quantize, applyPalette } from "gifenc";
import {
    SLIDING_ICON_TILES,
    SLIDING_HOME_HOLD_MS,
    SLIDING_STEP_MS,
    SLIDING_ROLL_STEPS,
    slidingIconPoseAt,
    slidingIconLoopMs,
    CTA_SLIDE_HOLD_MS,
    CTA_SLIDE_EXIT_MS,
} from "./SlidingAnimatedIcon";

export const SLIDING_ADMOB_BANNER_GIF_W = 320;
export const SLIDING_ADMOB_BANNER_GIF_H = 50;
export const SLIDING_ADMOB_BANNER_GIF_FILE =
    "sliding-puzzle-quest-admob-banner-320x50.gif";

function roundRect(ctx, x, y, w, h, r) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
}

function drawTile(ctx, x, y, size, colorTop, colorBot, radius) {
    const g = ctx.createLinearGradient(x, y, x + size, y + size);
    g.addColorStop(0, colorTop);
    g.addColorStop(1, colorBot);
    ctx.fillStyle = g;
    roundRect(ctx, x, y, size, size, radius);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.28)";
    roundRect(ctx, x + 1, y + 1, size - 2, size * 0.35, radius * 0.6);
    ctx.fill();
}

function drawAnimatedIcon(ctx, ox, oy, size, pose) {
    const pad = Math.max(2, size * 0.08);
    const gap = Math.max(1.5, size * 0.045);
    const cell = (size - pad * 2 - gap) / 2;
    const stride = cell + gap;
    const radius = Math.max(3, cell * 0.22);

    ctx.save();
    roundRect(ctx, ox, oy, size, size, size * 0.2);
    ctx.fillStyle = SLIDING_ICON_TILES.bg;
    ctx.fill();
    ctx.strokeStyle = "rgba(28,25,23,0.1)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.clip();

    // Dark well at home BR
    ctx.fillStyle = SLIDING_ICON_TILES.well;
    roundRect(ctx, ox + pad + stride, oy + pad + stride, cell, cell, radius);
    ctx.fill();

    const drawAt = (rc, colors, extraDy = 0, zHint = 0) => {
        const x = ox + pad + rc[1] * stride;
        const y = oy + pad + rc[0] * stride + extraDy * stride;
        drawTile(ctx, x, y, cell, colors[0], colors[1], radius);
        return zHint;
    };

    // Cream under colored tiles
    drawAt(pose.C, ["#FFFCFA", "#E8DFD0"]);
    drawAt(pose.R, ["#FF8A5C", "#E85A2A"]);
    drawAt(pose.G, ["#B8F04A", "#84CC16"]);
    drawAt(pose.B, ["#60A5FA", "#2563EB"], pose.Bdy || 0);

    ctx.restore();
}

/** Google Ads animated GIF: slower than 5 fps → delay > 200ms. */
const GOOGLE_ADS_MIN_FRAME_MS = 220;
/** Keep under the common 150 KB image-ad ceiling. */
const GOOGLE_ADS_MAX_GIF_BYTES = 150 * 1024;

function forceOpaqueRgba(rgba) {
    for (let i = 3; i < rgba.length; i += 4) {
        rgba[i] = 255;
    }
}

function drawBannerFrame(ctx, w, h, pose) {
    // Full opaque fill first — transparent corner pixels break many ad uploaders.
    ctx.fillStyle = "#FFF7ED";
    ctx.fillRect(0, 0, w, h);
    roundRect(ctx, 0, 0, w, h, 6);
    ctx.fill();
    ctx.strokeStyle = "#E7E5E4";
    ctx.lineWidth = 1.5;
    roundRect(ctx, 0.75, 0.75, w - 1.5, h - 1.5, 6);
    ctx.stroke();

    const accent = ctx.createLinearGradient(0, 0, 0, h);
    accent.addColorStop(0, "#F97316");
    accent.addColorStop(0.5, "#EA580C");
    accent.addColorStop(1, "#C2410C");
    ctx.fillStyle = accent;
    ctx.fillRect(0, 0, 4, h);

    const iconSize = 38;
    drawAnimatedIcon(ctx, 8, (h - iconSize) / 2, iconSize, pose);

    ctx.textBaseline = "middle";
    ctx.fillStyle = "#EA580C";
    ctx.font = "800 8px Segoe UI, Helvetica Neue, Arial, sans-serif";
    ctx.fillText("SLIDE · SOLVE", 54, 16);

    ctx.fillStyle = "#1C1917";
    ctx.font = "800 13px Segoe UI, Helvetica Neue, Arial, sans-serif";
    ctx.fillText("Sliding Puzzle Quest", 54, 30);

    const btnW = 68;
    const btnH = 30;
    const homeX = w - btnW - 6;
    const btnY = (h - btnH) / 2;
    // Match preview translateX(140%)
    const exitPx = btnW * 1.4;

    const drawGlossyPill = (btnX, isPlay) => {
        const label = isPlay ? "PLAY" : "SLIDE";
        ctx.save();
        ctx.shadowColor = isPlay ? "rgba(29,78,216,0.45)" : "rgba(194,65,12,0.4)";
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 2;
        const body = ctx.createLinearGradient(btnX, btnY, btnX, btnY + btnH);
        if (isPlay) {
            body.addColorStop(0, "#60A5FA");
            body.addColorStop(0.45, "#3B82F6");
            body.addColorStop(1, "#1D4ED8");
        } else {
            body.addColorStop(0, "#FB923C");
            body.addColorStop(0.45, "#F97316");
            body.addColorStop(1, "#C2410C");
        }
        ctx.fillStyle = body;
        roundRect(ctx, btnX, btnY, btnW, btnH, btnH / 2);
        ctx.fill();
        ctx.restore();

        const gloss = ctx.createLinearGradient(btnX, btnY + 2, btnX, btnY + btnH * 0.45);
        gloss.addColorStop(0, "rgba(255,255,255,0.4)");
        gloss.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = gloss;
        roundRect(ctx, btnX + 4, btnY + 2, btnW - 8, btnH * 0.42, btnH / 2);
        ctx.fill();

        ctx.strokeStyle = "rgba(255,255,255,0.28)";
        ctx.lineWidth = 1;
        roundRect(ctx, btnX + 0.5, btnY + 0.5, btnW - 1, btnH - 1, btnH / 2);
        ctx.stroke();

        const triX = btnX + 10;
        const triY = btnY + btnH / 2;
        ctx.fillStyle = "#FFFFFF";
        ctx.beginPath();
        ctx.moveTo(triX, triY - 4);
        ctx.lineTo(triX, triY + 4);
        ctx.lineTo(triX + 6.5, triY);
        ctx.closePath();
        ctx.fill();

        ctx.font = "800 10px Segoe UI, Helvetica Neue, Arial, sans-serif";
        ctx.fillStyle = "#FFFFFF";
        ctx.shadowColor = isPlay ? "rgba(30,64,175,0.45)" : "rgba(154,52,18,0.45)";
        ctx.shadowBlur = 1;
        ctx.shadowOffsetY = 1;
        ctx.fillText(label, btnX + 20, btnY + btnH / 2);
        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
        ctx.shadowOffsetY = 0;
    };

    // Clip so SLIDE can slide off the right edge
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, w, h);
    ctx.clip();

    // PLAY only after SLIDE has fully exited (never behind SLIDE)
    if (pose.ctaShowPlay) {
        drawGlossyPill(homeX, true);
    }
    if (pose.ctaShowSlide) {
        const slideX = homeX + exitPx * (pose.ctaExitT || 0);
        drawGlossyPill(slideX, false);
    }

    ctx.restore();
}

/**
 * Icon motion samples + dedicated CTA slide-out keyframes.
 * Google Ads requires <5 fps → every frame delay ≥ 220ms (gifenc uses ms).
 */
function sampleTimes(interps) {
    const msList = [0];

    for (let i = 0; i < SLIDING_ROLL_STEPS.length - 1; i++) {
        const base = SLIDING_HOME_HOLD_MS + i * SLIDING_STEP_MS;
        for (let k = 0; k < interps.length; k++) {
            msList.push(base + SLIDING_STEP_MS * interps[k]);
        }
    }
    msList.push(slidingIconLoopMs() - 1);

    const exitKeys = [CTA_SLIDE_HOLD_MS];
    const exitFrames = 5;
    for (let i = 1; i < exitFrames; i++) {
        exitKeys.push(CTA_SLIDE_HOLD_MS + (CTA_SLIDE_EXIT_MS * i) / exitFrames);
    }
    exitKeys.push(CTA_SLIDE_HOLD_MS + CTA_SLIDE_EXIT_MS - 1);
    exitKeys.push(CTA_SLIDE_HOLD_MS + CTA_SLIDE_EXIT_MS);

    msList.sort((a, b) => a - b);
    const deduped = [];
    for (let i = 0; i < msList.length; i++) {
        const ms = msList[i];
        if (!deduped.length || ms - deduped[deduped.length - 1] >= 90) {
            deduped.push(ms);
        }
    }

    // Always keep CTA exit poses (don't let icon samples drop them)
    for (let i = 0; i < exitKeys.length; i++) {
        const key = exitKeys[i];
        let nearest = -1;
        let nearestDist = Infinity;
        for (let j = 0; j < deduped.length; j++) {
            const d = Math.abs(deduped[j] - key);
            if (d < nearestDist) {
                nearestDist = d;
                nearest = j;
            }
        }
        if (nearest >= 0 && nearestDist < 50) {
            deduped[nearest] = key;
        } else {
            deduped.push(key);
        }
    }
    deduped.sort((a, b) => a - b);

    const times = [];
    for (let i = 0; i < deduped.length; i++) {
        const ms = deduped[i];
        const next = deduped[i + 1];
        const delayMs = next
            ? Math.max(GOOGLE_ADS_MIN_FRAME_MS, Math.round(next - ms))
            : 800;
        times.push({ ms, delayMs });
    }
    return times;
}

/**
 * @param {{ ms:number, delayMs:number }[]} samples
 * @param {number} maxColors
 */
async function encodeGifBytes(samples, maxColors) {
    const w = SLIDING_ADMOB_BANNER_GIF_W;
    const h = SLIDING_ADMOB_BANNER_GIF_H;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true, alpha: false });
    if (!ctx) throw new Error("Canvas 2D not available");

    /** @type {Uint8ClampedArray[]} */
    const framesRgba = [];

    for (let i = 0; i < samples.length; i++) {
        const pose = slidingIconPoseAt(samples[i].ms);
        ctx.fillStyle = "#FFF7ED";
        ctx.fillRect(0, 0, w, h);
        drawBannerFrame(ctx, w, h, pose);
        const rgba = ctx.getImageData(0, 0, w, h).data;
        forceOpaqueRgba(rgba);
        framesRgba.push(new Uint8ClampedArray(rgba));
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 0));
    }

    // Quantize from a spaced subset so palette covers motion without a huge strip.
    const stride = Math.max(1, Math.floor(framesRgba.length / 8));
    const pickCount = Math.ceil(framesRgba.length / stride);
    const strip = new Uint8ClampedArray(w * h * 4 * pickCount);
    let pick = 0;
    for (let i = 0; i < framesRgba.length; i += stride) {
        strip.set(framesRgba[i], pick * w * h * 4);
        pick += 1;
    }
    // Always include last frame (PLAY CTA).
    strip.set(framesRgba[framesRgba.length - 1], (pickCount - 1) * w * h * 4);
    const palette = quantize(strip, maxColors, { format: "rgb565" });

    const gif = GIFEncoder();
    for (let i = 0; i < framesRgba.length; i++) {
        const index = applyPalette(framesRgba[i], palette, "rgb565");
        const delayMs = Math.max(GOOGLE_ADS_MIN_FRAME_MS, samples[i].delayMs);
        gif.writeFrame(index, w, h, {
            ...(i === 0 ? { palette, repeat: 1 } : {}),
            delay: delayMs,
            dispose: 2,
        });
    }

    gif.finish();
    return gif.bytes();
}

export async function encodeSlidingAdMobBannerGif() {
    // Prefer smoother motion; fall back if file exceeds Google Ads ~150 KB.
    const attempts = [
        { interps: [0.25, 0.5, 0.75, 1], colors: 48 },
        { interps: [0.34, 0.67, 1], colors: 48 },
        { interps: [0.5, 1], colors: 64 },
        { interps: [1], colors: 64 },
    ];

    let lastBytes = null;
    for (let a = 0; a < attempts.length; a++) {
        const { interps, colors } = attempts[a];
        const samples = sampleTimes(interps);
        // eslint-disable-next-line no-await-in-loop
        const bytes = await encodeGifBytes(samples, colors);
        lastBytes = bytes;
        if (bytes.byteLength <= GOOGLE_ADS_MAX_GIF_BYTES) {
            return new Blob([bytes], { type: "image/gif" });
        }
    }

    throw new Error(
        `GIF is ${(lastBytes.byteLength / 1024).toFixed(1)} KB; Google Ads max is 150 KB.`
    );
}

export async function downloadSlidingAdMobBannerGif(fileName = SLIDING_ADMOB_BANNER_GIF_FILE) {
    const blob = await encodeSlidingAdMobBannerGif();
    const objectUrl = URL.createObjectURL(blob);
    try {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = fileName;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } finally {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    }
}
